import type { ChatMessage } from '../types/chat-message.js';
import type {
  LinkPreviewData,
  MessageAttachment,
  ResolvedMessageMedia,
} from './types.js';
import { classifyMediaUrl, extractUrlsFromText } from './classify-url.js';
import { guessFileNameFromUrl } from './file-kind.js';
import {
  captionWithoutUrls,
  createLinkPreviewFromUrl,
  mergeLinkPreviewsForUrls,
} from './link-preview.js';
import { areEquivalentMediaUrls } from './normalize-media-url.js';
import { parseVideoEmbedUrl } from './video-embed.js';

function isRedundantCaption(
  text: string,
  attachment?: MessageAttachment,
): boolean {
  const trimmed = text.trim();
  if (!trimmed) return true;
  if (attachment?.url && areEquivalentMediaUrls(trimmed, attachment.url)) {
    return true;
  }

  const urls = extractUrlsFromText(trimmed);
  if (urls.length === 1 && urls[0] === trimmed) return true;

  if (attachment?.kind === 'video_embed') {
    const embed = parseVideoEmbedUrl(trimmed);
    if (embed && embed.watchUrl === attachment.url) return true;
  }

  return false;
}

function resolveCaption(
  text: string | undefined,
  attachment?: MessageAttachment,
  urls: string[] = [],
): string | undefined {
  const trimmed = text?.trim() ?? '';
  if (isRedundantCaption(trimmed, attachment)) return undefined;
  if (urls.length > 0) {
    return captionWithoutUrls(trimmed, urls);
  }
  return trimmed || undefined;
}

function attachmentFromUrl(
  url: string,
  kind: ReturnType<typeof classifyMediaUrl>,
  fileName?: string,
): MessageAttachment | undefined {
  if (!kind) return undefined;

  if (kind === 'video_embed') {
    const embed = parseVideoEmbedUrl(url);
    if (!embed) return undefined;
    return {
      kind: 'video_embed',
      url: embed.watchUrl,
      embedUrl: embed.embedUrl,
      provider: embed.provider,
      fileName: fileName ?? embed.provider,
    };
  }

  return {
    kind,
    url,
    fileName: fileName ?? guessFileNameFromUrl(url),
  };
}

function linkPreviewsForText(
  text: string,
  messagePreviews?: LinkPreviewData[],
  excludeUrl?: string,
): LinkPreviewData[] {
  const urls = extractUrlsFromText(text).filter(
    (url) => !excludeUrl || !areEquivalentMediaUrls(url, excludeUrl),
  );
  if (urls.length === 0) return messagePreviews ?? [];
  return mergeLinkPreviewsForUrls(urls, messagePreviews);
}

/**
 * Resolve rich media presentation for a chat message.
 * Pure function — safe to unit test and swap independently of UI.
 */
export function resolveMessageMedia(message: ChatMessage): ResolvedMessageMedia {
  if (message.attachment) {
    const text = message.text?.trim() ?? '';
    const linkPreviews = linkPreviewsForText(
      text,
      message.linkPreviews,
      message.attachment.url,
    );

    return {
      attachment: message.attachment,
      linkPreviews,
      caption: resolveCaption(message.text, message.attachment, extractUrlsFromText(text)),
    };
  }

  const mediaUrl = message.mediaUrl?.trim();
  const contentType = message.contentType ?? 'text';

  if (mediaUrl) {
    const kind =
      contentType === 'image'
        ? 'image'
        : contentType === 'video'
          ? classifyMediaUrl(mediaUrl) ?? 'video'
          : contentType === 'voice'
            ? 'audio'
            : contentType === 'file'
              ? 'file'
              : classifyMediaUrl(mediaUrl);

    const attachment = attachmentFromUrl(mediaUrl, kind ?? 'file');
    const text = message.text?.trim() ?? '';
    const linkPreviews = linkPreviewsForText(text, message.linkPreviews, mediaUrl);

    return {
      attachment,
      linkPreviews,
      caption: resolveCaption(message.text, attachment, extractUrlsFromText(text)),
    };
  }

  const text = message.text?.trim() ?? '';
  const urls = extractUrlsFromText(text);
  const linkPreviews = linkPreviewsForText(text, message.linkPreviews);

  const soleUrl = urls.length === 1 ? urls[0] : undefined;
  const soleKind = soleUrl ? classifyMediaUrl(soleUrl) : null;
  const attachment =
    soleUrl && soleKind
      ? attachmentFromUrl(soleUrl, soleKind)
      : undefined;

  return {
    attachment,
    linkPreviews:
      attachment?.kind === 'video_embed' ? [] : linkPreviews,
    caption: resolveCaption(text, attachment, urls),
  };
}

// Re-export for tests and tooling
export { createLinkPreviewFromUrl } from './link-preview.js';
